import { useCallback, useContext, useEffect, useState } from 'react';
import { ApiService, AvailableAllocation } from 'api';
import { FUND_INVESTMENTS_TYPE } from 'common/constants/pageTypes';
import { useStore } from 'common/store';
import { LayoutContextValues, UseStoreValues } from 'common/types/store';
import ExcelExportContext from 'context/ExcelExportContext';
import LayoutContext from 'context/LayoutContext';
import { OtherOptions as OtherOptionsDisplay } from 'layouts/Main/components/ExcelExportDialogV2/BaseExcelExportDialog/components/OtherOptions';
import { useDocuments } from 'services/hooks';
import { formatDateView, Logger, useUserUtils } from 'utilities';
import { initialFormValues } from '../../ExcelExportDialog/constants';
import { useGenerateReports } from '../../ExcelExportDialog/utils';
import { BaseExcelExportDialog, FormulatedCheckbox } from '../BaseExcelExportDialog';
import {
  FundInvestmentCompanyExportOption as ExportOption,
  FUND_INVESTMENT_COMPANY_EXPORT_OPTIONS,
} from '../constants';
import { RadioOption } from '../FirmExcelExportDialog/components';
import { useExcelExportDataLoading, useExcelExportForm, useExcelExportValidation } from '../hooks';
import {
  FundInvestmentCompanyCurrentSelectedValues,
  OtherOptions,
  SelectedVersions,
} from './FundInvestmentCompanyExcelExportDialog.types';
import {
  CompanyExcelExportDialogErrors,
  HasDocumentsData,
} from '../CompanyExcelExportDialog/CompanyExcelExportDialog.types';
import { BaseDialogProps, ExcelExportContextType, ExcelExportFormValues } from '../types';
import { isFormValid } from '../validations';

const FundInvestmentCompanyExcelExportDialog = ({ open, onClose }: BaseDialogProps) => {
  const [{ companyInfo }] = useStore() as unknown as UseStoreValues;
  const { currentMeasurementDate, pageType } = useContext(LayoutContext) as unknown as LayoutContextValues;
  const {
    isLoading: isExportLoading,
    currentSelectedValues: valuesSelectedExternally,
    setCurrentSelectedValues,
  } = useContext(ExcelExportContext) as unknown as ExcelExportContextType;
  const { hasDocuments: checkHasDocuments } = useDocuments();

  const [selectedOption, setSelectedOption] = useState<ExportOption>(
    FUND_INVESTMENT_COMPANY_EXPORT_OPTIONS.FUND_INVESTMENT
  );
  const [includeDocuments, setIncludeDocuments] = useState(true);
  const [formulated, setFormulated] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [hasSummary, setHasSummary] = useState(false);
  const [hasMarketAdjustment, setHasMarketAdjustment] = useState(false);
  const [hasDocuments, setHasDocuments] = useState(false);

  const [otherOptions, setOtherOptions] = useState<OtherOptions>({
    summary: false,
    marketAdjustment: false,
    documents: false,
  });

  const [selectedVersions, setSelectedVersions] = useState<SelectedVersions>({
    fundInvestmentOwnership: null,
    marketAdjustment: null,
  });

  const currentSelectedValues = valuesSelectedExternally;
  const { name: company_name, legal_company_name: legalCompanyName } = companyInfo ?? {};
  const companyName = company_name || legalCompanyName || 'Fund investment';
  const { isStaffUser } = useUserUtils();

  const { formValues, updateFormValues } = useExcelExportForm({
    initialFormValues: initialFormValues as ExcelExportFormValues,
  });

  const { errors, setErrors } = useExcelExportValidation<CompanyExcelExportDialogErrors>({
    initialErrors: {} as CompanyExcelExportDialogErrors,
  });

  const { isLoading, dataLoadError, loadData } = useExcelExportDataLoading({
    isOpen: open,
    onError: error => Logger.error('Data loading error:', error),
  });

  // Reset state when dialog is opened to ensure fresh start
  useEffect(() => {
    if (open) {
      // Reset form values to initial state
      updateFormValues(initialFormValues);
      // Clear any previous errors
      setErrors({} as CompanyExcelExportDialogErrors);

      // Reset other state variables
      setSelectedOption(FUND_INVESTMENT_COMPANY_EXPORT_OPTIONS.FUND_INVESTMENT);
      setIncludeDocuments(true);
      setFormulated(true);
      setSelectedVersion(null);
      setOtherOptions({
        summary: false,
        marketAdjustment: false,
        documents: false,
      });
      setSelectedVersions({
        marketAdjustment: null,
        fundInvestmentOwnership: null,
      });
    }
    // Disabling exhaustive deps to prevent infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, updateFormValues, setErrors]);

  useEffect(() => {
    updateFormValues({
      export_summary: Boolean(hasSummary),
      export_market_adjustment: Boolean(currentSelectedValues.market_adjustment),
      export_documents: Boolean(hasDocuments),
      fund_investment_ownership: currentSelectedValues.fund_investment_ownership,
      market_adjustment: currentSelectedValues.market_adjustment,
      export_not_formulated: !formulated,
      include_documents_and_notes_from_tabs: includeDocuments,
      report_date: currentMeasurementDate?.date ?? null,
    });
  }, [
    hasSummary,
    hasDocuments,
    formulated,
    includeDocuments,
    selectedVersion,
    currentSelectedValues,
    updateFormValues,
    currentMeasurementDate?.date,
  ]);

  const { generateReport } = useGenerateReports({
    pageType,
    firmInfo: null,
    companyInfo,
    fundInfo: null,
    currentMeasurementDateId: currentMeasurementDate?.id,
    setErrors: setErrors as any,
    formValues,
  });

  const handleExport = useCallback(async () => {
    if (selectedOption === FUND_INVESTMENT_COMPANY_EXPORT_OPTIONS.FUND_INVESTMENT && selectedVersion) {
      await generateReport(formValues);
    } else if (selectedOption === FUND_INVESTMENT_COMPANY_EXPORT_OPTIONS.OTHER) {
      const {
        summary: export_summary,
        marketAdjustment: export_market_adjustment,
        documents: export_documents,
      } = otherOptions;

      const { marketAdjustment, fundInvestmentOwnership } = selectedVersions;

      const otherFormValues = {
        fund_investment_ownership: fundInvestmentOwnership,
        market_adjustment: marketAdjustment,
        export_documents,
        export_summary,
        export_market_adjustment,
        export_not_formulated: !formulated,
        export_formulated_and_print_ready: false,
        include_documents_and_notes_from_tabs: includeDocuments,
        report_date: currentMeasurementDate?.date ?? null,
      };
      await generateReport(otherFormValues);
    }
  }, [
    selectedOption,
    selectedVersion,
    selectedVersions,
    formValues,
    generateReport,
    otherOptions,
    formulated,
    includeDocuments,
    currentMeasurementDate?.date,
  ]);

  const hasAtLeastOneReport = hasSummary || hasDocuments || hasMarketAdjustment;
  const hasAtLeastOneOtherOption = Object.values(otherOptions).some(Boolean);

  const isValidExportSelection =
    isFormValid(errors) &&
    hasAtLeastOneReport &&
    ((selectedOption === FUND_INVESTMENT_COMPANY_EXPORT_OPTIONS.FUND_INVESTMENT && selectedVersion) ||
      (selectedOption === FUND_INVESTMENT_COMPANY_EXPORT_OPTIONS.OTHER && hasAtLeastOneOtherOption));

  const isExportDisabled = isLoading || isExportLoading || !isValidExportSelection;

  const fetchAllocationVersions = async (
    companyId: number,
    measurementDateId: number
  ): Promise<AvailableAllocation[]> => {
    try {
      const result = await ApiService.apiFundInvestmentsExportDataAvailabilityRead(
        companyId.toString(),
        measurementDateId.toString()
      );
      return result;
    } catch (error) {
      Logger.error('Error loading allocation versions', error);
      return [];
    }
  };

  const fetchDocumentsData = async (companyId: number, measurementDateId: number): Promise<HasDocumentsData> => {
    try {
      const result = await checkHasDocuments(companyId, measurementDateId);
      return (result as HasDocumentsData) || { has_documents: false };
    } catch (error) {
      Logger.error('Error loading documents', error);
      return { has_documents: false };
    }
  };

  const parseAllocations = (allocationVersions: AvailableAllocation[]) => ({
    parsedValuations: allocationVersions.map(alloc => ({
      ...alloc,
      id: alloc.valuation,
      is_main: alloc.is_main || alloc.version === 1 || allocationVersions.length === 1,
      name: `Version ${alloc.version} - ${formatDateView(alloc.name) as string}`,
    })) as AvailableAllocation[],
  });

  const setCompanyState = (hasDocumentsData: HasDocumentsData, mainValuation: AvailableAllocation | null) => {
    setHasDocuments(hasDocumentsData?.has_documents || false);
    const mainVersion = mainValuation?.id || null;
    setHasSummary(mainValuation?.has_summary || false);
    setHasMarketAdjustment(Boolean(mainValuation?.market_adjustments?.length));
    setSelectedVersion(mainVersion);
    setSelectedVersions({
      marketAdjustment: mainValuation?.market_adjustments?.[0].id ?? null,
      fundInvestmentOwnership: mainValuation?.fund_investment_ownership ?? null,
    });
  };

  const setContextValues = (hasDocumentsData: HasDocumentsData, mainValuation: AvailableAllocation | null) => {
    const contextValues: FundInvestmentCompanyCurrentSelectedValues = {
      market_adjustment: mainValuation?.market_adjustments?.[0].id ?? null,
      fund_investment_ownership: mainValuation?.fund_investment_ownership ?? null,
      valuation: mainValuation?.id ?? null,
      export_market_adjustment: Boolean(mainValuation?.market_adjustments?.length),
      export_documents: Boolean(hasDocumentsData?.has_documents),
      export_summary: Boolean(mainValuation?.fund_investment_ownership),
    };
    // this is what will be used when using the first option (Fund Investment)
    setCurrentSelectedValues(contextValues);
  };

  useEffect(() => {
    const loadCompanyData = async () => {
      if (!open || !companyInfo?.id || !currentMeasurementDate?.id || pageType !== FUND_INVESTMENTS_TYPE) {
        return;
      }

      await loadData(async () => {
        // for this export I need to know the fund investment ownership id and market adjustment id
        const [allocationVersions, hasDocumentsData] = await Promise.all([
          fetchAllocationVersions(companyInfo.id!, currentMeasurementDate.id!),
          fetchDocumentsData(companyInfo.id!, currentMeasurementDate.id!),
        ]);
        const parsedData = parseAllocations(allocationVersions);
        const mainValuation = parsedData.parsedValuations.find(alloc => alloc.is_main) ?? null;

        setCompanyState(hasDocumentsData, mainValuation);
        setContextValues(hasDocumentsData, mainValuation);
      });
    };

    loadCompanyData();
    // Disabling exhaustive deps to prevent infinite re-renders and unnecessary API calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyInfo?.id, currentMeasurementDate?.id, pageType]);

  const title = `${companyName} Excel Export`;

  const handleSetSelectedOption = useCallback(
    (option: string) => {
      setSelectedOption(option as ExportOption);
    },
    [setSelectedOption]
  );

  const handleSetOtherOptions = useCallback(
    (options: Record<string, boolean>) => {
      setOtherOptions(options as OtherOptions);
    },
    [setOtherOptions]
  );

  const renderSubOptions = (currentSelectedOption: string) => (
    <>
      <RadioOption value={FUND_INVESTMENT_COMPANY_EXPORT_OPTIONS.FUND_INVESTMENT} label="Fund Investment" />
      {isStaffUser() && (
        <FormulatedCheckbox
          formulated={formulated}
          setFormulated={setFormulated}
          selectedOption={currentSelectedOption}
          showCondition={currentSelectedOption === FUND_INVESTMENT_COMPANY_EXPORT_OPTIONS.FUND_INVESTMENT}
        />
      )}
      <RadioOption value={FUND_INVESTMENT_COMPANY_EXPORT_OPTIONS.OTHER} label="Other" />
    </>
  );

  const renderOtherOptions = (currentSelectedOption: string) => (
    <OtherOptionsDisplay
      otherOptions={otherOptions}
      showCondition={currentSelectedOption === FUND_INVESTMENT_COMPANY_EXPORT_OPTIONS.OTHER}
      setOtherOptions={handleSetOtherOptions}
      selectedOption={currentSelectedOption}
      options={[
        { key: 'summary', label: 'Summary', disabled: !hasSummary },
        { key: 'marketAdjustment', label: 'Market Adjustment', disabled: !hasMarketAdjustment },
        { key: 'documents', label: 'Documents', disabled: !hasDocuments },
      ]}
    />
  );

  return (
    <BaseExcelExportDialog
      open={open}
      onClose={onClose}
      title={title}
      selectedOption={selectedOption}
      setSelectedOption={handleSetSelectedOption}
      formulated={formulated}
      setFormulated={setFormulated}
      otherOptions={otherOptions}
      setOtherOptions={handleSetOtherOptions}
      hasAtLeastOneReport={hasAtLeastOneReport}
      hasAtLeastOneOtherOption={hasAtLeastOneOtherOption}
      isOtherOptionValid={true}
      errors={errors}
      setErrors={setErrors}
      dataLoadError={dataLoadError}
      isLoading={isLoading}
      isExportDisabled={isExportDisabled}
      isExportLoading={isExportLoading}
      onExport={handleExport}
      renderSubOptions={renderSubOptions}
      renderOtherOptions={renderOtherOptions}
    />
  );
};

export default FundInvestmentCompanyExcelExportDialog;
